import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { chatbotApi, formatApiError, llmApi } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ArrowLeft, Loader2, Save, Trash2, Plus, Upload, X, MessageCircle, User, Code, Copy, Check, ExternalLink } from "lucide-react";
import { PageHeader, SectionCard } from "../components/ui/page-shell";
import { toast } from "sonner";
import ModelPicker from "../components/ModelPicker";
import LeadRecipientsPanel from "../components/LeadRecipientsPanel";
import CoOwnersPanel from "../components/CoOwnersPanel";

const LLM_PROVIDERS = [
  { value: "1minai", label: "1min.AI", models: ["gpt-4o", "gpt-4o-mini", "claude-sonnet-4-5", "gemini-2.5-flash", "deepseek-chat"], placeholder: "...", keyLabel: "1min.AI API Key", freeformModel: true },
  { value: "openai",     label: "OpenAI",        models: ["gpt-4o", "gpt-4o-mini", "gpt-4-turbo"], placeholder: "sk-...",        keyLabel: "OpenAI API Key" },
  { value: "anthropic",  label: "Anthropic",     models: ["claude-opus-4-1", "claude-sonnet-4-5", "claude-haiku-4-5"], placeholder: "sk-ant-...", keyLabel: "Anthropic API Key" },
  { value: "gemini",     label: "Google Gemini", models: ["gemini-flash-latest", "gemini-2.5-flash", "gemini-2.5-flash-lite", "gemini-2.5-pro"], placeholder: "AIza...",    keyLabel: "Google Gemini API Key" },
  { value: "grok",       label: "xAI Grok",      models: ["grok-2-latest", "grok-2-mini"], placeholder: "xai-...",        keyLabel: "xAI Grok API Key" },
  { value: "deepseek",   label: "DeepSeek",      models: ["deepseek-flash", "deepseek-chat", "deepseek-reasoner"], placeholder: "sk-...",   keyLabel: "DeepSeek API Key", freeformModel: true },
  { value: "qwen",       label: "Alibaba Qwen (Model Studio)", models: ["qwen3.7-flash", "qwen3.8-flash", "qwen3.7-plus"], placeholder: "sk-...", keyLabel: "Alibaba Model Studio API Key (Singapore)", freeformModel: true, costTier: "economy" },
  { value: "zai",        label: "Z.ai GLM",      models: [], placeholder: "...", keyLabel: "Z.ai API Key", freeformModel: true },
  { value: "moonshot",   label: "Moonshot Kimi", models: [], placeholder: "sk-...", keyLabel: "Moonshot API Key", freeformModel: true },
  { value: "mistral",    label: "Mistral AI",    models: [], placeholder: "...", keyLabel: "Mistral API Key", freeformModel: true },
  { value: "openrouter", label: "OpenRouter",    models: ["qwen/qwen3.7-flash", "deepseek/deepseek-v4.1-flash", "z-ai/glm-5.3-flash", "openai/gpt-6-luna", "google/gemini-3.8-flash", "anthropic/claude-sonnet-5.5"], placeholder: "sk-or-...", keyLabel: "OpenRouter API Key", freeformModel: true },
  { value: "groq",       label: "Groq (fast & cheap)", models: ["llama-3.3-70b-versatile", "llama-3.1-8b-instant", "mixtral-8x7b-32768", "gemma2-9b-it"], placeholder: "gsk_...", keyLabel: "Groq API Key", freeformModel: true, costTier: "economy" },
  { value: "together",   label: "Together AI",        models: ["meta-llama/Llama-3.3-70B-Instruct-Turbo", "meta-llama/Llama-3.1-8B-Instruct-Turbo", "mistralai/Mixtral-8x7B-Instruct-v0.1", "Qwen/Qwen2.5-72B-Instruct-Turbo"], placeholder: "...", keyLabel: "Together AI API Key", freeformModel: true, costTier: "economy" },
];

const LANGUAGES = [
  { value: "ms", label: "Bahasa Melayu" },
  { value: "en", label: "English" },
  { value: "es", label: "Spanish" },
  { value: "fr", label: "French" },
  { value: "de", label: "German" },
  { value: "ja", label: "Japanese" }
];

const FONT_OPTIONS = [
  { value: "IBM Plex Sans", label: "IBM Plex Sans" },
  { value: "Inter", label: "Inter" },
  { value: "Roboto", label: "Roboto" },
  { value: "Open Sans", label: "Open Sans" },
  { value: "Nunito", label: "Nunito" },
  { value: "Plus Jakarta Sans", label: "Plus Jakarta Sans" }
];

const BUBBLE_STYLES = [
  { value: "rounded", label: "Rounded" },
  { value: "square", label: "Square" },
  { value: "pill", label: "Pill" }
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function DeployTab({ chatbot, id, copied, setCopied }) {
  const embedScript = `<script src="${BACKEND_URL}/api/widget/${id}" async></script>`;
  const iframeCode = `<iframe src="${BACKEND_URL}/chat/${id}" width="400" height="600" style="border:none;border-radius:12px;box-shadow:0 8px 32px rgba(0,0,0,0.18);" allow="microphone"></iframe>`;
  const directLink = `${BACKEND_URL}/chat/${id}`;
  const SL_THEMES = ["classic","midnight","sunset","forest","bubble","neon","ember","royal","retro","mono_dots","rose_gold","ocean"];
  const [slTheme, setSlTheme] = useState("classic");
  const [slCustom, setSlCustom] = useState("");
  const [slCreating, setSlCreating] = useState(false);
  const [slLinks, setSlLinks] = useState([]);
  const loadShortLinks = useCallback(async () => {
    try { const r = await chatbotApi.listShortLinks(id); setSlLinks(r.data?.items || []); } catch (e) {}
  }, [id]);
  useEffect(() => { loadShortLinks(); }, [loadShortLinks]);
  const createShortLink = async () => {
    setSlCreating(true);
    try {
      await chatbotApi.createShortLink(id, { theme: slTheme, slug: slCustom || undefined });
      setSlCustom("");
      toast.success("Short link & QR created");
      await loadShortLinks();
    } catch (e) { toast.error(formatApiError(e)); }
    setSlCreating(false);
  };
  const deleteShortLink = async (linkId) => {
    try { await chatbotApi.deleteShortLink(linkId); await loadShortLinks(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const downloadQr = (dataUrl, slug) => {
    const a = document.createElement("a"); a.href = dataUrl; a.download = `qr-${slug}.png`; a.click();
  };

  const note = "The script auto-detects its hosting URL, so it works on any domain where your BICARRA is deployed.";

  const copyToClipboard = (text, label) => {
    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(label);
      toast.success(`${label} copied to clipboard`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy. Please select and copy manually.");
    }
  };

  const isReady = chatbot?.status === "live";

  return (
    <div className="space-y-6">
      {/* Status */}
      {!isReady && (
        <div className="border border-[hsl(var(--warning-yellow))]/30 bg-[hsl(var(--warning-yellow))]/5 p-4" data-testid="deploy-warning">
          <p className="font-medium text-sm">Your chatbot is not ready for deployment</p>
          <p className="text-sm text-muted-foreground mt-1">
            {chatbot?.status !== "live" && "Train your chatbot first to make it live. "}
          </p>
        </div>
      )}

      {/* Script Embed */}
      <div className="border border-border p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 flex items-center justify-center">
            <Code className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="font-semibold">Embed Script</h2>
            <p className="text-sm text-muted-foreground">Add this to your website's HTML — a floating chat bubble will appear</p>
          </div>
        </div>
        <div className="relative">
          <pre className="bg-[#0A0A0A] text-[#E0E0E0] p-4 text-sm font-mono overflow-x-auto whitespace-pre-wrap break-all" data-testid="embed-script-code">
            {embedScript}
          </pre>
          <Button
            size="sm"
            variant="ghost"
            className="absolute top-2 right-2 h-7 px-2 bg-white/10 hover:bg-white/20 text-white"
            onClick={() => copyToClipboard(embedScript, "script")}
            data-testid="copy-script-btn"
          >
            {copied === "script" ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
            {copied === "script" ? "Copied" : "Copy"}
          </Button>
        </div>
      </div>

      {/* Iframe Embed */}
      <div className="border border-border p-6 space-y-4">
        <div>
          <h2 className="font-semibold">Iframe Embed</h2>
          <p className="text-sm text-muted-foreground">Embed the chat directly inside a section of your page</p>
        </div>
        <div className="relative">
          <pre className="bg-[#0A0A0A] text-[#E0E0E0] p-4 text-sm font-mono overflow-x-auto whitespace-pre-wrap break-all" data-testid="embed-iframe-code">
            {iframeCode}
          </pre>
          <Button
            size="sm"
            variant="ghost"
            className="absolute top-2 right-2 h-7 px-2 bg-white/10 hover:bg-white/20 text-white"
            onClick={() => copyToClipboard(iframeCode, "iframe")}
            data-testid="copy-iframe-btn"
          >
            {copied === "iframe" ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
            {copied === "iframe" ? "Copied" : "Copy"}
          </Button>
        </div>
      </div>

      {/* Direct Link */}
      <div className="border border-border p-6 space-y-4">
        <div>
          <h2 className="font-semibold">Direct Link</h2>
          <p className="text-sm text-muted-foreground">Share this link directly with users</p>
        </div>
        <div className="flex items-center gap-2">
          <Input value={directLink} readOnly className="rounded-none font-mono text-sm" data-testid="direct-link-input" />
          <Button variant="outline" className="rounded-none shrink-0" onClick={() => copyToClipboard(directLink, "link")} data-testid="copy-link-btn">
            {copied === "link" ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
          <a href={directLink} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="rounded-none shrink-0" data-testid="open-link-btn">
              <ExternalLink className="w-4 h-4" />
            </Button>
          </a>
        </div>
      </div>

        {/* Short Link & QR */}
        <div className="border border-border p-6 space-y-4" data-testid="shortlink-section">
          <div>
            <h2 className="font-semibold">Short Link & QR Code</h2>
            <p className="text-sm text-muted-foreground">Create a Bicarra short link and QR code for this chatbot's chat URL</p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex flex-col gap-1">
              <Label className="text-xs">QR Theme</Label>
              <Select value={slTheme} onValueChange={setSlTheme}>
                <SelectTrigger className="rounded-none w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SL_THEMES.map((t) => (<SelectItem key={t} value={t}>{t.replace("_"," ")}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1 flex-1 min-w-[160px]">
              <Label className="text-xs">Custom name (optional)</Label>
              <Input value={slCustom} onChange={(e) => setSlCustom(e.target.value)} placeholder="e.g. myassistant" className="rounded-none font-mono text-sm" />
            </div>
            <Button className="rounded-none" onClick={createShortLink} disabled={slCreating} data-testid="create-shortlink-btn">
              {slCreating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Generate
            </Button>
          </div>
          {slLinks.length > 0 && (
            <div className="space-y-3 pt-2">
              {slLinks.map((l) => (
                <div key={l.id} className="flex items-center gap-3 border border-border p-3">
                  {l.qr_data_url && <img src={l.qr_data_url} alt="QR" className="w-16 h-16 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-sm truncate">{l.short_url}</div>
                    <div className="text-xs text-muted-foreground">{l.clicks || 0} clicks &middot; {l.qr_theme}</div>
                  </div>
                  <Button size="sm" variant="outline" className="rounded-none shrink-0" onClick={() => copyToClipboard(l.short_url, l.id)}>
                    {copied === l.id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-none shrink-0" onClick={() => downloadQr(l.qr_data_url, l.slug)}>
                    <Upload className="w-4 h-4 rotate-180" />
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-none shrink-0" onClick={() => deleteShortLink(l.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      {/* Instructions */}
      <div className="border border-border p-6 space-y-3">
        <h2 className="font-semibold">How to deploy</h2>
        <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
          <li>Make sure your chatbot is <strong className="text-foreground">trained</strong> and <strong className="text-foreground">public access</strong> is enabled</li>
          <li>Copy the <strong className="text-foreground">embed script</strong> above</li>
          <li>Paste it before the closing <code className="bg-muted px-1.5 py-0.5 font-mono text-xs">&lt;/body&gt;</code> tag of your website</li>
          <li>A floating chat bubble will appear in the bottom-right corner</li>
        </ol>
        <p className="text-xs text-muted-foreground mt-3 p-2 bg-muted/50">{note}</p>
      </div>
    </div>
  );
}

const DEFAULT_LEAD_FIELDS = [
  { field_name: "name", label: "Your Name", required: true, field_type: "text" },
  { field_name: "email", label: "Email Address", required: true, field_type: "email" },
  { field_name: "phone", label: "Phone Number", required: false, field_type: "tel" },
  { field_name: "company", label: "Company", required: false, field_type: "text" }
];

export default function ChatbotSettings() {
  const { id } = useParams();
  const [chatbot, setChatbot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ptAnalytics, setPtAnalytics] = useState(null);
  const [ptAnalyticsLoading, setPtAnalyticsLoading] = useState(false);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const navigate = useNavigate();
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [copied, setCopied] = useState(false);
  const avatarInputRef = useRef(null);

  const [form, setForm] = useState({
    name: "",
    description: "",
    system_prompt: "",
    llm_provider: "openai",
    model_name: "gpt-5.2",
    temperature: 0.7,
    language: "en",
    is_public: false,
    allowed_domains: "",
    // Widget customization
    avatar_url: "",
    welcome_message: "",
    faqs: [],
    // Theme
    appearance: {
      primary_color: "#0A0A0A",
      header_color: "#0A0A0A",
      text_color: "#FFFFFF",
      font_family: "IBM Plex Sans",
      bubble_style: "rounded",
      position: "bottom-right",
      launcher_icon: "default",
      show_branding: true
    },
    // Lead form
    lead_form_enabled: false,
    lead_form_title: "Before we start, please tell us about yourself",
    lead_form_fields: DEFAULT_LEAD_FIELDS,
    lead_form_submit_text: "Start Chat",
    lead_form_success_message: "Thanks! How can I help you today?",
    api_keys_masked: {},
    api_keys: {},
    // Batch 18: cache settings
    cache_enabled: true,
    cache_ttl_hours: 24,
    adab_rag_enabled: true,
    pii_filter_mode: "basic",
    pii_allowlist: "",
    openrouter_deny_data_collection: false
  });

  const [customProviders, setCustomProviders] = useState([]);
  const [fetchedModels, setFetchedModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadChatbot();
  }, [id]);

  useEffect(() => {
    (async () => {
      try {
        const res = await llmApi.listProviders();
        setCustomProviders(res.data?.custom || []);
      } catch (e) {
        setCustomProviders([]);
      }
    })();
  }, []);

  const loadChatbot = async () => {
    try {
      const res = await chatbotApi.get(id);
      const data = res.data;
      setChatbot(data);
      // Load health score in the background
      loadHealth();
      setForm({
        name: data.name || "",
        description: data.description || "",
        system_prompt: data.system_prompt || "",
        llm_provider: data.llm_provider || "openai",
        model_name: data.model_name || "gpt-5.2",
        temperature: data.temperature || 0.7,
        language: data.language || "en",
        is_public: data.is_public || false,
        allowed_domains: data.allowed_domains?.join("\n") || "",
        avatar_url: data.avatar_url || "",
        welcome_message: data.welcome_message || "Hello! How can I help you today?",
        faqs: data.faqs || [],
        appearance: {
          primary_color: data.appearance?.primary_color || "#0A0A0A",
          header_color: data.appearance?.header_color || data.appearance?.primary_color || "#0A0A0A",
          text_color: data.appearance?.text_color || "#FFFFFF",
          font_family: data.appearance?.font_family || "IBM Plex Sans",
          bubble_style: data.appearance?.bubble_style || "rounded",
          position: data.appearance?.position || "bottom-right",
          launcher_icon: data.appearance?.launcher_icon || "default",
          show_branding: data.appearance?.show_branding !== false
        },
        lead_form_enabled: data.lead_form_enabled || false,
        lead_form_title: data.lead_form_title || "Before we start, please tell us about yourself",
        lead_form_fields: (data.lead_form_fields && data.lead_form_fields.length > 0) ? data.lead_form_fields : DEFAULT_LEAD_FIELDS,
        lead_form_submit_text: data.lead_form_submit_text || "Start Chat",
        lead_form_success_message: data.lead_form_success_message || "Thanks! How can I help you today?",
        api_keys_masked: data.api_keys_masked || {},
        api_keys: {}, // pending changes, only sent on save
        cache_enabled: data.cache_enabled !== false,
      adab_rag_enabled: data.adab_rag_enabled !== false,
        pii_filter_mode: data.pii_filter_mode === "off" ? "off" : "basic",
        pii_allowlist: (data.pii_allowlist || []).join("\n"),
        openrouter_deny_data_collection: data.openrouter_deny_data_collection === true,
        cache_ttl_hours: data.cache_ttl_hours ?? 24,
      proactive_triggers: {
        enabled: data.proactive_triggers?.enabled || false,
        delay_seconds: data.proactive_triggers?.delay_seconds ?? 15,
        exit_intent: data.proactive_triggers?.exit_intent || false,
        scroll_percent: data.proactive_triggers?.scroll_percent ?? 0,
        message: data.proactive_triggers?.message || "👋 Need help? I'm here to chat!",
        show_once_per_session: data.proactive_triggers?.show_once_per_session !== false
      }
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const loadHealth = async () => {
    if (!id) return;
    setHealthLoading(true);
    try {
      const res = await chatbotApi.health(id);
      setHealth(res.data);
    } catch (e) {
      setHealth(null);
    } finally {
      setHealthLoading(false);
    }
  };

  const loadProactiveAnalytics = async () => {
    if (!id) return;
    setPtAnalyticsLoading(true);
    try {
      const res = await chatbotApi.proactiveAnalytics(id, 7);
      setPtAnalytics(res.data);
    } catch (e) {
      // Silent fail (likely just no events yet)
      setPtAnalytics({ totals: { bubble_shown: 0, bubble_clicked: 0, bubble_dismissed: 0, chat_opened: 0 }, ctr: 0, daily: [] });
    } finally {
      setPtAnalyticsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      const { api_keys_masked: _akm, api_keys: pendingKeys, ...rest } = form;
      const data = {
        ...rest,
        allowed_domains: form.allowed_domains.split("\n").filter(d => d.trim()),
        pii_allowlist: form.pii_allowlist.split("\n").map(s => s.trim()).filter(Boolean).slice(0, 50)
      };
      if (pendingKeys && Object.keys(pendingKeys).length > 0) {
        data.api_keys = pendingKeys;
      }
      const res = await chatbotApi.update(id, data);
      setChatbot(res.data);
      toast.success("Settings saved!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingAvatar(true);
    try {
      const res = await chatbotApi.uploadAvatar(id, file);
      setForm(prev => ({ ...prev, avatar_url: res.data.avatar_url }));
      toast.success("Avatar uploaded!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = "";
    }
  };

  const handleDeleteAvatar = async () => {
    try {
      await chatbotApi.deleteAvatar(id);
      setForm(prev => ({ ...prev, avatar_url: "" }));
      toast.success("Avatar deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const addFaq = () => {
    setForm(prev => ({
      ...prev,
      faqs: [...prev.faqs, { id: Date.now().toString(), question: "", answer: "" }]
    }));
  };

  const updateFaq = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      faqs: prev.faqs.map((faq, i) => i === index ? { ...faq, [field]: value } : faq)
    }));
  };

  const deleteFaq = (index) => {
    setForm(prev => ({
      ...prev,
      faqs: prev.faqs.filter((_, i) => i !== index)
    }));
  };

  const addLeadField = () => {
    setForm(prev => ({
      ...prev,
      lead_form_fields: [...prev.lead_form_fields, { field_name: `field_${prev.lead_form_fields.length + 1}`, label: "New Field", required: false, field_type: "text", placeholder: "" }]
    }));
  };

  const updateLeadField = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      lead_form_fields: prev.lead_form_fields.map((f, i) => i === index ? { ...f, [field]: value } : f)
    }));
  };

  const deleteLeadField = (index) => {
    setForm(prev => ({
      ...prev,
      lead_form_fields: prev.lead_form_fields.filter((_, i) => i !== index)
    }));
  };

  const allProviders = [
    ...LLM_PROVIDERS,
    ...customProviders
      .filter(cp => !LLM_PROVIDERS.some(p => p.value === cp.id))
      .map(cp => ({
        value: cp.id,
        label: `${cp.label || cp.id} (custom)`,
        models: [],
        placeholder: "sk-...",
        keyLabel: `${cp.label || cp.id} API Key`,
        freeformModel: true,
        isCustom: true,
      })),
  ];
  const selectedProvider = allProviders.find(p => p.value === form.llm_provider);
  const modelOptions = fetchedModels.length > 0
    ? fetchedModels
    : (selectedProvider?.models || []);

  const handleLoadModels = async () => {
    if (!form.llm_provider) return;
    setLoadingModels(true);
    try {
      const res = await llmApi.listModels(form.llm_provider);
      const list = res.data?.models || [];
      setFetchedModels(list);
      if (list.length === 0) toast.error("Provider returned no models");
      else toast.success(`Loaded ${list.length} models`);
    } catch (e) {
      toast.error("Could not load models from this provider");
    } finally {
      setLoadingModels(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const isCoOwner = chatbot?.access_role === "co_owner";
  // BC-020: viewer and agent roles get a limited card instead of settings.
  const isViewer = chatbot?.access_role === "viewer";
  const isAgent = chatbot?.access_role === "agent";

  const handleLeave = async () => {
    if (leaving) return;
    if (!window.confirm("Leave this shared chatbot? You will lose access immediately.")) return;
    setLeaving(true);
    try {
      await chatbotApi.leaveShared(id);
      toast.success("You left this chatbot");
      navigate("/home");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to leave this chatbot");
      setLeaving(false);
    }
  };

  // BC-020: viewers and agents get a limited card instead of the full settings form.
  if (isViewer || isAgent) {
    const roleLabel = isAgent ? "Agent" : "Viewer";
    const whatYouCanDo = isAgent
      ? "You can see leads and conversations, reply to chats, take over conversations, and update conversation and lead status, tags, and notes."
      : "You can see leads and conversations, and export them.";
    return (
      <div className="space-y-6" data-testid="chatbot-settings">
        <div className="flex items-center gap-4">
          <Link to={`/chatbot/${id}`}>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </Link>
          <div className="flex-1">
            <PageHeader title="Chatbot Settings" subtitle="Configure behavior, branding, integrations, and embed." />
            <p className="text-muted-foreground">{chatbot?.name}</p>
          </div>
        </div>
        <div
          className="border border-border bg-muted/40 p-6 space-y-4 max-w-xl"
          data-testid="shared-role-limited-card"
        >
          <h2 className="font-semibold">You have {roleLabel} access to this assistant</h2>
          <p className="text-sm text-muted-foreground">
            {whatYouCanDo} The owner's workspace pays for this assistant and manages its API keys.
          </p>
          <div className="flex flex-wrap gap-2">
            <Link to={`/chatbot/${id}`}>
              <Button variant="outline" size="sm" className="rounded-none">
                Conversations
              </Button>
            </Link>
            <Button
              variant="outline"
              size="sm"
              className="rounded-none"
              onClick={handleLeave}
              disabled={leaving}
              data-testid="co-owner-leave-btn"
            >
              {leaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Leave
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="chatbot-settings">
      {/* BC-019: co-owners see a notice instead of owner-only sections */}
      {isCoOwner && (
        <div
          className="border border-border bg-muted/40 p-4 flex flex-wrap items-center justify-between gap-3"
          data-testid="co-owner-notice"
        >
          <p className="text-sm text-muted-foreground min-w-0">
            Shared with you. The owner's workspace pays for this assistant and manages its API keys.
          </p>
          <Button
            variant="outline"
            size="sm"
            className="rounded-none shrink-0"
            onClick={handleLeave}
            disabled={leaving}
            data-testid="co-owner-leave-btn"
          >
            {leaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Leave
          </Button>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to={`/chatbot/${id}`}>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <ArrowLeft className="w-4 h-4" />
          </Button>
        </Link>
        <div className="flex-1">
          <PageHeader title="Chatbot Settings" subtitle="Configure behavior, branding, integrations, and embed." />
          <p className="text-muted-foreground">{chatbot?.name}</p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      <Tabs defaultValue="basic" className="space-y-6">
        <TabsList className={`grid w-full max-w-4xl rounded-none ${isCoOwner ? "grid-cols-7" : "grid-cols-8"}`}>
          <TabsTrigger value="basic" className="rounded-none">Basic</TabsTrigger>
          <TabsTrigger value="widget" className="rounded-none">Widget</TabsTrigger>
          <TabsTrigger value="theme" className="rounded-none">Theme</TabsTrigger>
          <TabsTrigger value="faqs" className="rounded-none">FAQs</TabsTrigger>
          <TabsTrigger value="leads" className="rounded-none">Lead Form</TabsTrigger>
          <TabsTrigger value="ratings" className="rounded-none" data-testid="ratings-tab">Ratings</TabsTrigger>
              <TabsTrigger value="deploy" className="rounded-none" data-testid="deploy-tab">Deploy</TabsTrigger>
          {!isCoOwner && (
            <TabsTrigger value="sharing" className="rounded-none" data-testid="sharing-tab">Sharing</TabsTrigger>
          )}
        </TabsList>

        {/* Basic Settings */}
        <TabsContent value="basic" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="border border-border p-6 space-y-6">
              <h2 className="font-semibold">Basic Information</h2>
              
              <div className="space-y-2">
                <Label className="overline">Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="My Chatbot"
                  className="rounded-none"
                  data-testid="chatbot-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="overline">Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="What does this chatbot do?"
                  className="rounded-none"
                />
              </div>

              <div className="space-y-2">
                <Label className="overline">System Prompt</Label>
                <Textarea
                  value={form.system_prompt}
                  onChange={(e) => setForm({ ...form, system_prompt: e.target.value })}
                  placeholder="You are a helpful AI assistant..."
                  className="rounded-none min-h-[150px]"
                  data-testid="system-prompt-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="overline">Language</Label>
                <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                  <SelectTrigger className="rounded-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map(lang => (
                      <SelectItem key={lang.value} value={lang.value}>{lang.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border border-border p-6 space-y-6">
              <h2 className="font-semibold">AI Model Settings</h2>

              <ModelPicker
                currentProvider={form.llm_provider}
                currentModel={form.model_name}
                onPick={({ provider, model }) => {
                  setForm({ ...form, llm_provider: provider, model_name: model });
                  setFetchedModels([]);
                  const label = allProviders.find(p => p.value === provider)?.label || provider;
                  toast.success(`${model} selected via ${label}. Make sure a ${label} API key is saved, then click Save.`);
                }}
              />

              <div className="space-y-2">
                <Label className="overline">LLM Provider</Label>
                <Select value={form.llm_provider} onValueChange={(v) => { setForm({ ...form, llm_provider: v, model_name: allProviders.find(p => p.value === v)?.models[0] || "" }); setFetchedModels([]); }}>
                  <SelectTrigger className="rounded-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {allProviders.map(provider => (
                      <SelectItem key={provider.value} value={provider.value}>{provider.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="overline">Model</Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleLoadModels}
                    disabled={loadingModels}
                    className="rounded-none h-7 text-xs"
                    data-testid="load-models-btn"
                  >
                    {loadingModels ? "Loading..." : "Load models from provider"}
                  </Button>
                </div>
                <Select value={form.model_name} onValueChange={(v) => setForm({ ...form, model_name: v })}>
                  <SelectTrigger className="rounded-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {modelOptions.map(model => (
                      <SelectItem key={model} value={model}>{model}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(selectedProvider?.freeformModel || selectedProvider?.isCustom) && (
                  <Input
                    value={form.model_name || ""}
                    onChange={(e) => setForm({ ...form, model_name: e.target.value })}
                    placeholder="or type a model id, e.g. openai/gpt-4o-mini"
                    className="rounded-none mt-2 font-mono text-xs"
                    data-testid="model-freetext"
                  />
                )}
              {selectedProvider?.costTier === "economy" && (
                <p className="text-xs text-muted-foreground mt-2 p-2 bg-muted/50 rounded">
                  ⚡ Economy tier — fast inference at a fraction of the cost of premium models. Great for high-volume chatbot use cases.
                </p>
              )}
              {form.llm_provider === "openai" && form.model_name && ["gpt-4o", "gpt-4-turbo", "gpt-5", "gpt-5-turbo"].includes(form.model_name) && (
                <p className="text-xs text-amber-700 mt-2 p-2 bg-amber-50 rounded border border-amber-200">
                  💡 Cost-saving tip: Switch to <strong>gpt-4o-mini</strong> for ~95% lower cost. Usually sufficient for chatbot tasks.
                </p>
              )}
              {form.llm_provider === "anthropic" && form.model_name && ["claude-opus-4-1", "claude-sonnet-4-5"].includes(form.model_name) && (
                <p className="text-xs text-amber-700 mt-2 p-2 bg-amber-50 rounded border border-amber-200">
                  💡 Cost-saving tip: Switch to <strong>claude-haiku-4-5</strong> for much lower cost on routine queries.
                </p>
              )}
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="overline">Temperature</Label>
                  <span className="text-sm font-medium">{form.temperature}</span>
                </div>
                <Slider
                  value={[form.temperature]}
                  onValueChange={([v]) => setForm({ ...form, temperature: v })}
                  min={0}
                  max={1}
                  step={0.1}
                  className="w-full"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Public Access</Label>
                  <p className="text-xs text-muted-foreground">Allow anyone to chat</p>
                </div>
                <Switch
                  checked={form.is_public}
                  onCheckedChange={(v) => setForm({ ...form, is_public: v })}
                  data-testid="public-switch"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Malaysian Adab RAG</Label>
                  <p className="text-xs text-muted-foreground">Ground replies in Malaysian adab & akhlak (default on)</p>
                </div>
                <Switch
                  checked={form.adab_rag_enabled}
                  onCheckedChange={(v) => setForm({ ...form, adab_rag_enabled: v })}
                  data-testid="adab-rag-switch"
                />
              </div>

              <div className="space-y-2">
                <Label className="overline">Privacy filter</Label>
                <p className="text-xs text-muted-foreground">Before a visitor message is sent to the AI provider, IC numbers, phone numbers, emails, card numbers, bank accounts and passport numbers are replaced with placeholders. Recommended on.</p>
                <Select
                  value={form.pii_filter_mode}
                  onValueChange={(v) => setForm({ ...form, pii_filter_mode: v })}
                  data-testid="pii-filter-mode"
                >
                  <SelectTrigger className="rounded-none">
                    <SelectValue placeholder="Choose a mode" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="off">Off</SelectItem>
                    <SelectItem value="basic">Basic</SelectItem>
                  </SelectContent>
                </Select>
                {form.pii_filter_mode !== "off" && (
                  <div className="space-y-1">
                    <Label className="overline">Never filter these (one per line)</Label>
                    <Textarea
                      value={form.pii_allowlist}
                      onChange={(e) => setForm({ ...form, pii_allowlist: e.target.value })}
                      placeholder={"03-1234 5678\nhello@yourbusiness.com"}
                      className="rounded-none font-mono text-sm"
                      data-testid="pii-allowlist"
                    />
                    <p className="text-xs text-muted-foreground">Values that are never filtered from visitor messages, such as your own business phone or email.</p>
                  </div>
                )}
                <div className="flex items-center justify-between pt-1">
                  <div>
                    <Label className="text-sm font-medium">OpenRouter: only use providers that do not store data</Label>
                    <p className="text-xs text-muted-foreground">Applies when this assistant uses OpenRouter. Some models have no such provider and will return an error.</p>
                  </div>
                  <Switch
                    checked={form.openrouter_deny_data_collection}
                    onCheckedChange={(v) => setForm({ ...form, openrouter_deny_data_collection: v })}
                    data-testid="openrouter-deny-switch"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="overline">Allowed Domains</Label>
                <Textarea
                  value={form.allowed_domains}
                  onChange={(e) => setForm({ ...form, allowed_domains: e.target.value })}
                  placeholder="example.com&#10;*.example.com"
                  className="rounded-none font-mono text-sm"
                />
              </div>
            </div>

          {/* Per-Chatbot API Keys */}
          {!isCoOwner && (
          <div className="space-y-3 border-t border-border pt-4">
            <div>
              <Label className="text-sm font-medium">Provider API Keys (optional)</Label>
              <p className="text-xs text-muted-foreground">Override the workspace default API keys for this chatbot only. Useful if you manage chatbots for clients with separate keys. Leave blank to use the workspace key.</p>
            </div>
            {LLM_PROVIDERS.map(({ value: provider, keyLabel: label, placeholder }) => ({ provider, label, placeholder })).map(({provider, label, placeholder}) => (
              <div key={provider} className="space-y-1">
                <Label className="overline">{label}</Label>
                <div className="flex gap-2">
                  <Input
                    type="password"
                    value={form.api_keys?.[provider] ?? ""}
                    onChange={(e) => setForm({ ...form, api_keys: { ...form.api_keys, [provider]: e.target.value } })}
                    placeholder={form.api_keys_masked?.[provider] ? `Saved: ${form.api_keys_masked[provider]}` : placeholder}
                    className="font-mono text-xs"
                    data-testid={`apikey-${provider}-input`}
                  />
                  {form.api_keys_masked?.[provider] && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setForm({ ...form, api_keys: { ...form.api_keys, [provider]: "" } })}
                      data-testid={`apikey-${provider}-clear`}
                    >
                      Clear
                    </Button>
                  )}
                </div>
                {form.api_keys?.[provider] === "" && form.api_keys_masked?.[provider] && (
                  <p className="text-xs text-destructive">Will be removed on save (falls back to workspace key)</p>
                )}
              </div>
            ))}
          </div>
          )}
          </div>

          {/* Chatbot Health Score */}
          <div className="border rounded-lg p-4 space-y-3" data-testid="health-score-card">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-semibold">Chatbot Health</Label>
                <p className="text-xs text-muted-foreground mt-1">A quick readiness check based on training, branding, and engagement.</p>
              </div>
              {health && (
                <div className="text-right">
                  <div className={`text-3xl font-bold ${health.grade === 'A' ? 'text-emerald-600' : health.grade === 'B' ? 'text-blue-600' : health.grade === 'C' ? 'text-amber-600' : 'text-red-600'}`}>{health.grade}</div>
                  <div className="text-xs text-muted-foreground">{health.percent}% &middot; {health.status}</div>
                </div>
              )}
              {!health && (
                <button type="button" onClick={loadHealth} className="text-xs text-blue-600 hover:underline" data-testid="reload-health-btn">
                  {healthLoading ? "Loading..." : "Refresh"}
                </button>
              )}
            </div>
            {health && (
              <div className="space-y-2">
                {health.checks.map((c) => (
                  <div key={c.key} className="flex items-start gap-2 text-sm">
                    <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${c.ok ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>
                      {c.ok ? "✓" : "!"}
                    </span>
                    <div className="flex-1">
                      <div className={c.ok ? "text-slate-900" : "text-slate-600"}>{c.label}</div>
                      {!c.ok && (
                        <div className="text-xs text-muted-foreground mt-0.5">{c.hint}</div>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground flex-shrink-0">{c.score}/{c.max}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="border border-border p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-semibold">Response Cache</h2>
              <p className="text-xs text-muted-foreground mt-1">Serve cached answers for repeat questions to cut API costs and speed up replies.</p>
            </div>
            <Switch
              checked={form.cache_enabled !== false}
              onCheckedChange={(v) => setForm({ ...form, cache_enabled: v })}
              data-testid="cache-enabled-switch"
            />
          </div>
          {form.cache_enabled !== false && (
            <div className="space-y-2">
              <Label className="overline">Cache TTL (hours): {form.cache_ttl_hours ?? 24}</Label>
              <Input
                type="number"
                min={1}
                max={720}
                value={form.cache_ttl_hours ?? 24}
                onChange={(e) => setForm({ ...form, cache_ttl_hours: parseInt(e.target.value) || 24 })}
                className="rounded-none"
                data-testid="cache-ttl-input"
              />
              <p className="text-xs text-muted-foreground">How long to keep cached answers. Default 24h. Max 30 days (720h).</p>
            </div>
          )}
        </div>
      </TabsContent>

        {/* Widget Customization */}
        <TabsContent value="widget" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="border border-border p-6 space-y-6">
              <h2 className="font-semibold">Chatbot Profile</h2>
              
              {/* Avatar Upload */}
              <div className="space-y-4">
                <Label className="overline">Profile Picture</Label>
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 bg-muted flex items-center justify-center overflow-hidden">
                    {form.avatar_url ? (
                      <img src={form.avatar_url} alt="Avatar" className="w-full h-full object-cover" />
                    ) : (
                      <MessageCircle className="w-8 h-8 text-muted-foreground" />
                    )}
                  </div>
                  <div className="space-y-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarUpload}
                      ref={avatarInputRef}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      onClick={() => avatarInputRef.current?.click()}
                      disabled={uploadingAvatar}
                      className="rounded-none"
                      data-testid="upload-avatar-btn"
                    >
                      {uploadingAvatar ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      Upload
                    </Button>
                    {form.avatar_url && (
                      <Button variant="ghost" onClick={handleDeleteAvatar} className="rounded-none text-[hsl(var(--signal-red))]">
                        <Trash2 className="w-4 h-4 mr-2" /> Remove
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="overline">Floating Button</Label>
                <label className="flex items-start gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="mt-1"
                    data-testid="launcher-icon-toggle"
                    disabled={!form.avatar_url}
                    checked={form.appearance.launcher_icon === "avatar"}
                    onChange={(e) => setForm({
                      ...form,
                      appearance: {
                        ...form.appearance,
                        launcher_icon: e.target.checked ? "avatar" : "default",
                      },
                    })}
                  />
                  <span className="text-sm">
                    Use the profile picture as the floating button
                    <span className="block text-xs text-muted-foreground mt-0.5">
                      {form.avatar_url
                        ? "The bubble on your website shows your picture instead of the default chat icon."
                        : "Upload a profile picture first to enable this."}
                    </span>
                  </span>
                </label>
              </div>

              <div className="space-y-2">
                <Label className="overline">Display Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Support Bot"
                  className="rounded-none"
                />
                <p className="text-xs text-muted-foreground">Shown in the chat widget header</p>
              </div>

              <div className="space-y-2">
                <Label className="overline">Welcome Message</Label>
                <Textarea
                  value={form.welcome_message}
                  onChange={(e) => setForm({ ...form, welcome_message: e.target.value })}
                  placeholder="Hello! How can I help you today?"
                  className="rounded-none"
                  data-testid="welcome-message-input"
                />
                <p className="text-xs text-muted-foreground">Automatically shown when chat opens</p>
              </div>
            </div>

            {/* Preview */}
            <div className="border border-border p-6">
              <h2 className="font-semibold mb-4">Widget Preview</h2>
              <div className="bg-muted rounded-lg overflow-hidden max-w-sm mx-auto shadow-lg">
                {/* Header */}
                <div 
                  className="p-4 flex items-center gap-3"
                  style={{ background: form.appearance.header_gradient || form.appearance.header_color || form.appearance.primary_color, color: form.appearance.text_color }}
                >
                  <div className="w-10 h-10 bg-white/20 flex items-center justify-center overflow-hidden rounded-full" style={form.appearance.accent ? { background: `radial-gradient(circle at 30% 30%, ${form.appearance.accent}, ${form.appearance.primary_color})`, boxShadow: `0 0 18px ${form.appearance.accent}66` } : {}}>
                    {form.avatar_url ? (
                      <img src={form.avatar_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <MessageCircle className="w-5 h-5" />
                    )}
                  </div>
                  <div>
                    <p className="font-semibold" style={{ fontFamily: form.appearance.font_family }}>{form.name || "Chatbot"}</p>
                    <p className="text-xs opacity-80">Online</p>
                  </div>
                </div>
                {/* Messages */}
                <div className="p-4 min-h-[200px]" style={{
              fontFamily: form.appearance.font_family,
              background: form.appearance.theme_preset === "midnight-aurora"
                ? "radial-gradient(800px 400px at 0% 0%, #1E1B4B22 0%, transparent 60%), radial-gradient(700px 350px at 100% 100%, #312E8133 0%, transparent 60%), #0B1220"
                : form.appearance.theme_preset === "webby-dark"
                ? "radial-gradient(600px 300px at 100% 0%, #FACC1518 0%, transparent 60%), #0A0A0A"
                : form.appearance.theme_preset === "sunny-yellow"
                ? "linear-gradient(180deg, #FFFBEB 0%, #FFFFFF 100%)"
                : form.appearance.theme_preset === "emerald-breeze"
                ? "#F9FAFB"
                : form.appearance.theme_preset === "coral-sunset"
                ? "#F9FAFB"
                : form.appearance.theme_preset === "lilac-bot"
                ? "linear-gradient(180deg, #FAF5FF 0%, #FFFFFF 100%)"
                : form.appearance.theme_preset === "pink-pulse"
                ? "radial-gradient(800px 400px at 0% 0%, #EC489922 0%, transparent 60%), radial-gradient(700px 350px at 100% 100%, #BE185D33 0%, transparent 60%), #0A0A0A"
                : (form.appearance.theme_preset ? "#FFFFFF" : undefined),
              color: form.appearance.theme_preset === "midnight-aurora" ? form.appearance.text_color : form.appearance.theme_preset === "webby-dark" ? "#FAFAFA" : form.appearance.theme_preset === "sunny-yellow" ? "#1F1300" : form.appearance.theme_preset === "lilac-bot" ? "#1E1033" : form.appearance.theme_preset === "pink-pulse" ? "#FFFFFF" : (form.appearance.theme_preset ? "#0F172A" : undefined)
            }}>
                  <div className="flex gap-2 mb-3">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={form.appearance.accent ? {
              background: `radial-gradient(circle at 30% 30%, ${form.appearance.accent}, ${form.appearance.primary_color})`,
              boxShadow: `0 0 8px ${form.appearance.accent}55`
            } : { background: "hsl(var(--muted))" }}>
                      {form.avatar_url ? (
                        <img src={form.avatar_url} alt="" className="w-full h-full object-cover rounded-full" />
                      ) : (
                        <MessageCircle className="w-3 h-3" />
                      )}
                    </div>
                    <div 
                      className={`p-2 text-sm ${form.appearance.bubble_style === 'pill' ? 'rounded-full px-4' : form.appearance.bubble_style === 'square' ? '' : 'rounded-lg'}`} style={
              form.appearance.theme_preset === "midnight-aurora"
                ? { background: "rgba(124, 156, 255, 0.14)", color: form.appearance.text_color, border: "1px solid rgba(165, 180, 252, 0.18)" }
                : form.appearance.theme_preset === "webby-dark"
                ? { background: "rgba(255,255,255,0.06)", color: "#FAFAFA", border: "1px solid rgba(250, 204, 21, 0.20)" }
                : form.appearance.theme_preset === "sunny-yellow"
                ? { background: "#FFFFFF", color: "#1F1300", border: "1px solid #FCD34D" }
                : form.appearance.theme_preset === "emerald-breeze"
                ? { background: "#FFFFFF", color: "#0F172A", border: "1px solid #6EE7B7" }
                : form.appearance.theme_preset === "coral-sunset"
                ? { background: "#FFFFFF", color: "#0F172A", border: "1px solid #FCA5A5" }
                : form.appearance.theme_preset === "lilac-bot"
                ? { background: "#F5F0FF", color: "#1E1033", border: "1px solid #DDD6FE" }
                : form.appearance.theme_preset === "pink-pulse"
                ? { background: "rgba(236, 72, 153, 0.10)", color: "#FFFFFF", border: "1px solid rgba(244, 114, 182, 0.25)" }
                : (form.appearance.theme_preset ? { background: "#F1F5FF", color: "#0F172A", border: "1px solid #E2E8F8" } : { background: "hsl(var(--muted))" })
            }
                    >
                      {form.welcome_message || "Hello! How can I help?"}
                    </div>
                  </div>
                  {form.faqs.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {form.faqs.slice(0, 3).map((faq) => (
                        <button key={faq.question || faq.answer} className="text-xs px-3 py-1.5 border border-border hover:bg-muted transition-colors" style={{ borderColor: form.appearance.primary_color, color: form.appearance.primary_color }}>
                          {faq.question?.substring(0, 30) || "FAQ Question"}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Proactive Triggers */}
          <div className="border rounded-lg p-4 space-y-4" data-testid="proactive-triggers-section">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-semibold">Proactive Triggers</Label>
                <p className="text-xs text-muted-foreground mt-1">Show a friendly message bubble to engage visitors automatically.</p>
              </div>
              <Switch
                checked={form.proactive_triggers?.enabled || false}
                onCheckedChange={(v) => setForm({ ...form, proactive_triggers: { ...form.proactive_triggers, enabled: v } })}
                data-testid="proactive-enabled-switch"
              />
            </div>
            {form.proactive_triggers?.enabled && (
              <div className="space-y-4 pt-2 border-t">
                <div>
                  <Label className="text-sm">Message</Label>
                  <Input type="text" value={form.proactive_triggers?.message || ""} onChange={(e) => setForm({ ...form, proactive_triggers: { ...form.proactive_triggers, message: e.target.value } })} placeholder="👋 Need help? I'm here to chat!" maxLength={120} data-testid="proactive-message-input" />
                  <p className="text-xs text-muted-foreground mt-1">Shown in a preview bubble next to the chat launcher.</p>
                </div>
                {/* Mini Analytics Dashboard */}
                <div className="bg-slate-50 border rounded-lg p-3" data-testid="proactive-analytics-card">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-semibold">Last 7 Days</Label>
                    <button type="button" onClick={loadProactiveAnalytics} className="text-xs text-blue-600 hover:underline" data-testid="reload-analytics-btn">
                      {ptAnalyticsLoading ? "Loading..." : (ptAnalytics ? "Refresh" : "Load stats")}
                    </button>
                  </div>
                  {ptAnalytics && (
                    <div className="grid grid-cols-4 gap-2 text-center">
                      <div>
                        <div className="text-lg font-semibold text-slate-900">{ptAnalytics.totals?.bubble_shown ?? 0}</div>
                        <div className="text-xs text-muted-foreground">Shown</div>
                      </div>
                      <div>
                        <div className="text-lg font-semibold text-blue-600">{ptAnalytics.totals?.bubble_clicked ?? 0}</div>
                        <div className="text-xs text-muted-foreground">Clicked</div>
                      </div>
                      <div>
                        <div className="text-lg font-semibold text-slate-500">{ptAnalytics.totals?.bubble_dismissed ?? 0}</div>
                        <div className="text-xs text-muted-foreground">Dismissed</div>
                      </div>
                      <div>
                        <div className="text-lg font-semibold text-emerald-600">{ptAnalytics.ctr ?? 0}%</div>
                        <div className="text-xs text-muted-foreground">CTR</div>
                      </div>
                    </div>
                  )}
                  {!ptAnalytics && !ptAnalyticsLoading && (
                    <div className="text-xs text-muted-foreground text-center py-2">Click "Load stats" to see how visitors are engaging.</div>
                  )}
                </div>
                <div>
                  <Label className="text-sm">Delay (seconds on page)</Label>
                  <Input type="number" min={0} max={300} value={form.proactive_triggers?.delay_seconds ?? 15} onChange={(e) => setForm({ ...form, proactive_triggers: { ...form.proactive_triggers, delay_seconds: parseInt(e.target.value) || 0 } })} data-testid="proactive-delay-input" />
                  <p className="text-xs text-muted-foreground mt-1">Show after this many seconds. Set 0 to disable the timer.</p>
                </div>
                <div>
                  <Label className="text-sm">Scroll trigger (% of page scrolled)</Label>
                  <Input type="number" min={0} max={100} value={form.proactive_triggers?.scroll_percent ?? 0} onChange={(e) => setForm({ ...form, proactive_triggers: { ...form.proactive_triggers, scroll_percent: parseInt(e.target.value) || 0 } })} data-testid="proactive-scroll-input" />
                  <p className="text-xs text-muted-foreground mt-1">Show after visitor scrolls this percent. Set 0 to disable.</p>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Exit intent (desktop)</Label>
                    <p className="text-xs text-muted-foreground">Show when the mouse leaves the top of the page.</p>
                  </div>
                  <Switch checked={form.proactive_triggers?.exit_intent || false} onCheckedChange={(v) => setForm({ ...form, proactive_triggers: { ...form.proactive_triggers, exit_intent: v } })} data-testid="proactive-exit-switch" />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Show only once per session</Label>
                    <p className="text-xs text-muted-foreground">Avoid annoying repeat visitors.</p>
                  </div>
                  <Switch checked={form.proactive_triggers?.show_once_per_session !== false} onCheckedChange={(v) => setForm({ ...form, proactive_triggers: { ...form.proactive_triggers, show_once_per_session: v } })} data-testid="proactive-once-switch" />
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Theme Settings */}
        <TabsContent value="theme" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="border border-border p-6 space-y-6">
              <h2 className="font-semibold">Colors</h2>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="overline">Primary Color</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={form.appearance.primary_color}
                      onChange={(e) => setForm({ ...form, appearance: { ...form.appearance, primary_color: e.target.value } })}
                      className="w-10 h-10 rounded-none border border-border cursor-pointer"
                    />
                    <Input
                      value={form.appearance.primary_color}
                      onChange={(e) => setForm({ ...form, appearance: { ...form.appearance, primary_color: e.target.value } })}
                      className="rounded-none flex-1"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="overline">Header Color</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={form.appearance.header_color || form.appearance.primary_color}
                      onChange={(e) => setForm({ ...form, appearance: { ...form.appearance, header_color: e.target.value } })}
                      className="w-10 h-10 rounded-none border border-border cursor-pointer"
                    />
                    <Input
                      value={form.appearance.header_color || form.appearance.primary_color}
                      onChange={(e) => setForm({ ...form, appearance: { ...form.appearance, header_color: e.target.value } })}
                      className="rounded-none flex-1"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="overline">Text Color</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={form.appearance.text_color}
                      onChange={(e) => setForm({ ...form, appearance: { ...form.appearance, text_color: e.target.value } })}
                      className="w-10 h-10 rounded-none border border-border cursor-pointer"
                    />
                    <Input
                      value={form.appearance.text_color}
                      onChange={(e) => setForm({ ...form, appearance: { ...form.appearance, text_color: e.target.value } })}
                      className="rounded-none flex-1"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="overline">Font Family</Label>
                <Select value={form.appearance.font_family} onValueChange={(v) => setForm({ ...form, appearance: { ...form.appearance, font_family: v } })}>
                  <SelectTrigger className="rounded-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FONT_OPTIONS.map(font => (
                      <SelectItem key={font.value} value={font.value}>{font.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="overline">Bubble Style</Label>
                <Select value={form.appearance.bubble_style} onValueChange={(v) => setForm({ ...form, appearance: { ...form.appearance, bubble_style: v } })}>
                  <SelectTrigger className="rounded-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BUBBLE_STYLES.map(style => (
                      <SelectItem key={style.value} value={style.value}>{style.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Show Branding</Label>
                  <p className="text-xs text-muted-foreground">Display "Powered by BICARRA"</p>
                </div>
                <Switch
                  checked={form.appearance.show_branding}
                  onCheckedChange={(v) => setForm({ ...form, appearance: { ...form.appearance, show_branding: v } })}
                />
              </div>
            </div>

            <div className="border border-border p-6 mb-6">
              <h2 className="font-semibold mb-1">Theme Presets</h2>
              <p className="text-xs text-muted-foreground mb-4">One-click styling for the chat widget</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  {
                    id: "sky-mint",
                    name: "Sky Mint",
                    desc: "Friendly bright blue with a teal kiss",
                    primary_color: "#2563EB",
                    header_color: "#38BDF8",
                    header_gradient: "linear-gradient(135deg, #5CC6FF 0%, #2563EB 100%)",
                    text_color: "#FFFFFF",
                    font_family: "Inter",
                    bubble_style: "rounded",
                    accent: "#0EA5E9"
                  },
                  {
                    id: "midnight-aurora",
                    name: "Midnight Aurora",
                    desc: "Glassy dark with a soft indigo glow",
                    primary_color: "#7C9CFF",
                    header_color: "#0B1220",
                    header_gradient: "linear-gradient(135deg, #0B1220 0%, #1E1B4B 60%, #312E81 100%)",
                    text_color: "#E6EDFF",
                    font_family: "Plus Jakarta Sans",
                    bubble_style: "rounded",
                    accent: "#A5B4FC"
                  },
                  {
                    id: "sunny-yellow",
                    name: "Sunny Yellow",
                    desc: "Cheerful daylight with playful chips",
                    primary_color: "#7C3AED",
                    header_color: "#FACC15",
                    header_gradient: "linear-gradient(135deg, #FDE047 0%, #FACC15 60%, #F59E0B 100%)",
                    text_color: "#1F1300",
                    font_family: "Inter",
                    bubble_style: "rounded",
                    accent: "#FB923C"
                  },
                  {
                    id: "webby-dark",
                    name: "Webby Dark",
                    desc: "Sleek black with a sunny pop",
                    primary_color: "#FACC15",
                    header_color: "#0A0A0A",
                    header_gradient: "linear-gradient(135deg, #1A1A1A 0%, #0A0A0A 60%, #111111 100%)",
                    text_color: "#FAFAFA",
                    font_family: "Inter",
                    bubble_style: "rounded",
                    accent: "#FACC15"
                  },
                  {
                    id: "emerald-breeze",
                    name: "Emerald Breeze",
                    desc: "Crisp white with an emerald header",
                    primary_color: "#10B981",
                    header_color: "#10B981",
                    header_gradient: "linear-gradient(135deg, #34D399 0%, #10B981 60%, #059669 100%)",
                    text_color: "#FFFFFF",
                    font_family: "Inter",
                    bubble_style: "pill",
                    accent: "#6EE7B7"
                  },
                  {
                    id: "coral-sunset",
                    name: "Coral Sunset",
                    desc: "Charcoal header with warm coral pop",
                    primary_color: "#F97066",
                    header_color: "#1F2937",
                    header_gradient: "linear-gradient(135deg, #1F2937 0%, #111827 60%, #0F172A 100%)",
                    text_color: "#FFFFFF",
                    font_family: "Inter",
                    bubble_style: "rounded",
                    accent: "#FB7185"
                  },
                  {
                    id: "lilac-bot",
                    name: "Lilac Bot",
                    desc: "Playful purple with rounded chips",
                    primary_color: "#A855F7",
                    header_color: "#A855F7",
                    header_gradient: "linear-gradient(135deg, #C084FC 0%, #A855F7 60%, #9333EA 100%)",
                    text_color: "#FFFFFF",
                    font_family: "Inter",
                    bubble_style: "pill",
                    accent: "#E9D5FF"
                  },
                  {
                    id: "pink-pulse",
                    name: "Pink Pulse",
                    desc: "Premium dark with magenta glow",
                    primary_color: "#EC4899",
                    header_color: "#0A0A0A",
                    header_gradient: "linear-gradient(135deg, #EC4899 0%, #BE185D 50%, #1E1B4B 100%)",
                    text_color: "#FFFFFF",
                    font_family: "Plus Jakarta Sans",
                    bubble_style: "rounded",
                    accent: "#F472B6"
                  }
                ].map((theme) => (
                  <button
                    key={theme.id}
                    type="button"
                    onClick={() => setForm({ ...form, appearance: { ...form.appearance, primary_color: theme.primary_color, header_color: theme.header_color, header_gradient: theme.header_gradient, accent: theme.accent, text_color: theme.text_color, font_family: theme.font_family, bubble_style: theme.bubble_style, theme_preset: theme.id } })}
                    className={`text-left border transition-all hover:shadow-md rounded-lg overflow-hidden ${form.appearance?.theme_preset === theme.id ? "" : "border-border"}`}
                    style={form.appearance?.theme_preset === theme.id ? { boxShadow: `0 0 0 2px ${theme.primary_color}` } : {}}
                    data-testid={`theme-preset-${theme.id}`}
                  >
                    <div style={{ background: theme.header_gradient, color: theme.text_color, padding: "12px 14px", fontFamily: theme.font_family }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <div style={{ width: 28, height: 28, borderRadius: "50%", background: `radial-gradient(circle at 30% 30%, ${theme.accent}, ${theme.primary_color})`, boxShadow: `0 0 12px ${theme.accent}55` }} />
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.1 }}>{theme.name}</div>
                          <div style={{ fontSize: 10, opacity: 0.85 }}>Online</div>
                        </div>
                      </div>
                    </div>
                    <div style={{ background: theme.id === "midnight-aurora" ? "#0F172A" : "#FFFFFF", padding: "10px 14px", fontFamily: theme.font_family, color: theme.id === "midnight-aurora" ? "#E6EDFF" : "#0F172A", fontSize: 12 }}>
                      <div style={{ display: "inline-block", background: theme.primary_color, color: theme.text_color, padding: "6px 10px", borderRadius: 14, fontSize: 11 }}>Hi! How can I help?</div>
                      <div style={{ marginTop: 8, fontSize: 11, opacity: 0.7 }}>{theme.desc}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="border border-border p-6">
              <h2 className="font-semibold mb-4">Color Presets</h2>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { primary: "#0A0A0A", header: "#0A0A0A", text: "#FFFFFF" },
                  { primary: "#0047AB", header: "#0047AB", text: "#FFFFFF" },
                  { primary: "#059669", header: "#059669", text: "#FFFFFF" },
                  { primary: "#7C3AED", header: "#7C3AED", text: "#FFFFFF" },
                  { primary: "#DC2626", header: "#DC2626", text: "#FFFFFF" },
                  { primary: "#F97316", header: "#F97316", text: "#FFFFFF" },
                  { primary: "#0891B2", header: "#0891B2", text: "#FFFFFF" },
                  { primary: "#6366F1", header: "#6366F1", text: "#FFFFFF" }
                ].map((preset) => (
                  <button
                    key={preset.primary}
                    onClick={() => setForm({ ...form, appearance: { ...form.appearance, ...preset, header_color: preset.header, header_gradient: null, accent: null, theme_preset: null } })}
                    className="w-full aspect-square border border-border hover:scale-105 transition-transform"
                    style={{ backgroundColor: preset.primary }}
                  />
                ))}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* FAQ Buttons */}
        <TabsContent value="faqs" className="space-y-6">
          <div className="border border-border p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-semibold">FAQ Buttons</h2>
                <p className="text-sm text-muted-foreground">Quick answers without AI - displayed as clickable buttons</p>
              </div>
              <Button onClick={addFaq} variant="outline" className="rounded-none" data-testid="add-faq-btn">
                <Plus className="w-4 h-4 mr-2" /> Add FAQ
              </Button>
            </div>

            {form.faqs.length === 0 ? (
              <div className="text-center py-8 border border-dashed border-border">
                <p className="text-muted-foreground">No FAQs added yet</p>
                <p className="text-sm text-muted-foreground">FAQs provide instant answers without calling the AI</p>
              </div>
            ) : (
              <div className="space-y-4">
                {form.faqs.map((faq, index) => (
                  <div key={faq.id || index} className="border border-border p-4 space-y-3" data-testid={`faq-item-${index}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1 space-y-3">
                        <div className="space-y-1">
                          <Label className="overline">Question (Button Text)</Label>
                          <Input
                            value={faq.question}
                            onChange={(e) => updateFaq(index, "question", e.target.value)}
                            placeholder="What are your business hours?"
                            className="rounded-none"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="overline">Answer</Label>
                          <Textarea
                            value={faq.answer}
                            onChange={(e) => updateFaq(index, "answer", e.target.value)}
                            placeholder="We're open Monday-Friday, 9am-6pm PST."
                            className="rounded-none"
                          />
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteFaq(index)}
                        className="h-8 w-8 text-muted-foreground hover:text-[hsl(var(--signal-red))]"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Lead Form */}
        <TabsContent value="leads" className="space-y-6">
          <div className="border border-border p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-semibold">Lead Collection Form</h2>
                <p className="text-sm text-muted-foreground">Collect visitor information before they start chatting</p>
              </div>
              <Switch
                checked={form.lead_form_enabled}
                onCheckedChange={(v) => setForm({ ...form, lead_form_enabled: v })}
                data-testid="lead-form-toggle"
              />
            </div>

            {form.lead_form_enabled && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="overline">Form Title</Label>
                    <Input
                      value={form.lead_form_title}
                      onChange={(e) => setForm({ ...form, lead_form_title: e.target.value })}
                      placeholder="Before we start..."
                      className="rounded-none"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="overline">Submit Button Text</Label>
                    <Input
                      value={form.lead_form_submit_text || ""}
                      onChange={(e) => setForm({ ...form, lead_form_submit_text: e.target.value })}
                      placeholder="Start Chat"
                      className="rounded-none"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="overline">Success Message</Label>
                  <Input
                    value={form.lead_form_success_message || ""}
                    onChange={(e) => setForm({ ...form, lead_form_success_message: e.target.value })}
                    placeholder="Thanks! How can I help you today?"
                    className="rounded-none"
                  />
                  <p className="text-xs text-muted-foreground">Shown as the first assistant message after the form is submitted.</p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="overline">Form Fields</Label>
                      <p className="text-xs text-muted-foreground">Drag-free list. Add up to 10 fields. Field name is the data key, label is what visitors see.</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        onClick={() => setForm({ ...form, lead_form_fields: DEFAULT_LEAD_FIELDS })}
                        variant="ghost"
                        size="sm"
                        className="rounded-none"
                        data-testid="reset-lead-fields-btn"
                      >Reset to defaults</Button>
                      <Button
                        type="button"
                        onClick={addLeadField}
                        variant="outline"
                        size="sm"
                        className="rounded-none"
                        disabled={(form.lead_form_fields || []).length >= 10}
                        data-testid="add-lead-field-btn"
                      >
                        <Plus className="w-4 h-4 mr-1" /> Add Field
                      </Button>
                    </div>
                  </div>

                  {(form.lead_form_fields || []).length === 0 ? (
                    <div className="text-center py-8 border border-dashed border-border">
                      <p className="text-muted-foreground">No fields added yet</p>
                      <p className="text-sm text-muted-foreground">Click "Add Field" or "Reset to defaults" to get started.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {form.lead_form_fields.map((field, index) => (
                        <div key={field.field_name || `lead-field-${index}`} className="border border-border p-4 space-y-3 bg-muted/30" data-testid={`lead-field-${index}`}>
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-muted-foreground">Field #{index + 1}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteLeadField(index)}
                              className="h-7 w-7 text-muted-foreground hover:text-[hsl(var(--signal-red))]"
                              data-testid={`delete-lead-field-${index}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs">Label (visible to visitor)</Label>
                              <Input
                                value={field.label || ""}
                                onChange={(e) => updateLeadField(index, "label", e.target.value)}
                                placeholder="e.g. Your Name"
                                className="rounded-none"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Field Name (data key)</Label>
                              <Input
                                value={field.field_name || ""}
                                onChange={(e) => updateLeadField(index, "field_name", e.target.value.replace(/[^a-zA-Z0-9_]/g, "_"))}
                                placeholder="e.g. name"
                                className="rounded-none font-mono text-xs"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Field Type</Label>
                              <Select value={field.field_type} onValueChange={(v) => updateLeadField(index, "field_type", v)}>
                                <SelectTrigger className="rounded-none">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="text">Text</SelectItem>
                                  <SelectItem value="email">Email</SelectItem>
                                  <SelectItem value="tel">Phone</SelectItem>
                                  <SelectItem value="url">URL</SelectItem>
                                  <SelectItem value="number">Number</SelectItem>
                                  <SelectItem value="textarea">Textarea</SelectItem>
                                  <SelectItem value="select">Dropdown</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Placeholder</Label>
                              <Input
                                value={field.placeholder || ""}
                                onChange={(e) => updateLeadField(index, "placeholder", e.target.value)}
                                placeholder="Optional hint text"
                                className="rounded-none"
                              />
                            </div>
                            {field.field_type === "select" && (
                              <div className="md:col-span-2 space-y-1">
                                <Label className="text-xs">Options (one per line)</Label>
                                <Textarea
                                  value={(field.options || []).join("\n")}
                                  onChange={(e) => updateLeadField(index, "options", e.target.value.split("\n").map(o => o.trim()).filter(Boolean))}
                                  placeholder={"Sales\nMarketing\nSupport"}
                                  className="rounded-none min-h-[70px] font-mono text-xs"
                                />
                              </div>
                            )}
                            <div className="md:col-span-2 flex items-center gap-2">
                              <Switch
                                checked={!!field.required}
                                onCheckedChange={(v) => updateLeadField(index, "required", v)}
                              />
                              <Label className="text-xs">Required field</Label>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          {!isCoOwner && <LeadRecipientsPanel chatbotId={id} />}
        </TabsContent>

        {/* BC-019: Sharing tab, owner only */}
        <TabsContent value="sharing" className="space-y-6">
          <CoOwnersPanel chatbotId={id} />
        </TabsContent>

        {/* Deploy / Embed Tab */}
        <TabsContent value="ratings" className="space-y-6">
            <RatingsPanel chatbotId={id} />
          </TabsContent>
          <TabsContent value="deploy" className="space-y-6">
          <DeployTab chatbot={chatbot} id={id} copied={copied} setCopied={setCopied} />
        </TabsContent>
      </Tabs>

      {/* Danger Zone */}
      {!isCoOwner && (
      <div className="border border-[hsl(var(--signal-red))]/20 bg-[hsl(var(--signal-red))]/5 p-6">
        <h2 className="font-semibold text-[hsl(var(--signal-red))] mb-4">Danger Zone</h2>
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium">Delete Chatbot</p>
            <p className="text-sm text-muted-foreground">This action cannot be undone</p>
          </div>
          <Button
            variant="outline"
            className="border-[hsl(var(--signal-red))] text-[hsl(var(--signal-red))] hover:bg-[hsl(var(--signal-red))] hover:text-white rounded-none"
            onClick={async () => {
              if (deleting) return;
              if (!window.confirm("Are you sure you want to delete this chatbot? This action cannot be undone.")) return;
              setDeleting(true);
              try {
                await chatbotApi.delete(id);
                toast.success("Chatbot deleted");
                navigate("/my-chatbots", { replace: true });
              } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail) || "Failed to delete chatbot");
                setDeleting(false);
              }
            }}
            disabled={deleting}
            data-testid="delete-chatbot-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" /> {deleting ? "Deleting..." : "Delete"}
          </Button>
        </div>
      </div>
      )}
    </div>
  );
}

function RatingsPanel({ chatbotId }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!chatbotId) return;
    let cancelled = false;
    setLoading(true);
    chatbotApi.ratings(chatbotId, days).then((res) => {
      if (!cancelled) setData(res.data);
    }).catch(() => {}).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [chatbotId, days]);
  const total = data?.total || 0;
  const up = data?.up || 0;
  const down = data?.down || 0;
  const sat = data?.satisfaction ?? 0;
  return (
    <div className="space-y-4" data-testid="ratings-panel">
      <div className="flex items-center gap-3">
        <h3 className="text-base font-semibold">Message Ratings</h3>
        <select value={days} onChange={(e) => setDays(parseInt(e.target.value)||30)} className="border rounded px-2 py-1 text-sm" data-testid="ratings-days">
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>
      {loading && <p className="text-xs text-slate-500">Loading...</p>}
      <div className="grid grid-cols-4 gap-3">
        <div className="p-3 border rounded"><p className="text-xs text-slate-500">Total Ratings</p><p className="text-2xl font-semibold">{total}</p></div>
        <div className="p-3 border rounded"><p className="text-xs text-slate-500">Helpful</p><p className="text-2xl font-semibold text-green-600">{up}</p></div>
        <div className="p-3 border rounded"><p className="text-xs text-slate-500">Not Helpful</p><p className="text-2xl font-semibold text-rose-600">{down}</p></div>
        <div className="p-3 border rounded"><p className="text-xs text-slate-500">Satisfaction</p><p className="text-2xl font-semibold">{sat}%</p></div>
      </div>
      {Array.isArray(data?.recent_negative) && data.recent_negative.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold">Recent Negative Feedback</h4>
          {data.recent_negative.slice(0, 10).map((r, i) => (
            <div key={i} className="p-2 border rounded text-xs">
              <p className="text-slate-500">{r.created_at}</p>
              {r.comment && <p className="mt-1">{r.comment}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

